import { t } from '@lingui/macro'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'

export const AWELL_QUESTIONNAIRE_EVENTS: Record<'COMPLETED' | 'CANCELLED' | 'ERROR' | 'CLOSED', string> = {
  COMPLETED: 'awell-questionnaire-completed',
  CANCELLED: 'awell-questionnaire-cancelled',
  ERROR: 'awell-questionnaire-error',
  CLOSED: 'awell-questionnaire-closed',
}

export const AwellQuestionnaireCompletion = () => {
  // Hooks
  // ------------------------------------------------/

  const [searchParameters] = useSearchParams()

  // Template variables
  // ------------------------------------------------/
  const awellStatus = searchParameters.get('awellStatus')

  // Handlers
  // ------------------------------------------------/
  const handleClose = () => {
    postMessage(AWELL_QUESTIONNAIRE_EVENTS.CLOSED)
  }

  // Effects
  // ------------------------------------------------/
  /**
   * This effect is used to handle the status of the Awell questionnaire.
   * It will post a message to the parent window to notify the Awell SDK that the questionnaire has been completed, cancelled or failed.
   * It will also close the questionnaire if the status is cancelled.
   */
  useEffect(() => {
    if (awellStatus === 'success') {
      postMessage(AWELL_QUESTIONNAIRE_EVENTS.COMPLETED)
      return
    }

    if (awellStatus === 'cancelled') {
      postMessage(AWELL_QUESTIONNAIRE_EVENTS.CANCELLED)
      return
    }

    postMessage(AWELL_QUESTIONNAIRE_EVENTS.ERROR)
  }, [awellStatus])

  // Render
  // ------------------------------------------------/
  if (awellStatus === 'cancelled') {
    return null
  }

  return (
    <ContentPageLayout>
      <StContainer>
        {awellStatus === 'success' ? (
          <StImage src='/img/empty-pending-task-center.png' alt='Questionnaire completed' />
        ) : null}
        <StTextContainer>
          <StTitle>
            {awellStatus === 'success'
              ? t({
                  id: 'awellQuestionnaireCompletion.success.title',
                  message: 'Bedankt!',
                })
              : t({
                  id: 'awellQuestionnaireCompletion.error.title',
                  message: 'Er liep iets mis!',
                })}
          </StTitle>
          <StBody>
            {awellStatus === 'success'
              ? t({
                  id: 'awellQuestionnaireCompletion.success.body',
                  message: 'We hebben jouw antwoorden goed ontvangen!',
                })
              : t({
                  id: 'awellQuestionnaireCompletion.error.body',
                  message: 'Gelieve later opnieuw te proberen.',
                })}
          </StBody>
        </StTextContainer>

        <Button variant='Primary' onClick={handleClose}>
          {t({
            id: 'awellQuestionnaireCompletion.close',
            message: 'Sluiten',
          })}
        </Button>
      </StContainer>
    </ContentPageLayout>
  )
}

const StContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;

  max-width: 600px;
  margin: 0 auto;
  padding-bottom: var(--safe-area-inset-bottom, 0px);

  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StImage = styled.img`
  max-width: 220px;
  width: 50%;
  object-fit: cover;
  object-fit: cover;
`

const StTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StTitle = styled(H3)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-align: left;
  margin: 0;
`

const StBody = styled.span`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  font-size: ${({ theme }) => theme.Typography.BodyMediumRegular['font-size']};
  line-height: ${({ theme }) => theme.Typography.BodyMediumRegular['line-height']};
  font-weight: ${({ theme }) => theme.Typography.BodyMediumRegular['font-weight']};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};

  text-align: center;
`

function postMessage(message: string) {
  // InAppBrowser injects window.mobileApp for webview-to-native communication
  if ('mobileApp' in window) {
    ;(window as any).mobileApp.postMessage({ detail: { message } })
    return
  }

  const targetOrigin = window.location.origin
  window.parent.postMessage(message, targetOrigin)
}
